import { forwardRef, useId } from 'react';
import { cn, focusRing } from '../utils/cn';
import { Icon } from './Icon';

export interface DateRange {
  start: string | null;
  end: string | null;
}

export interface DateRangePickerProps {
  label: string;
  hideLabel?: boolean;
  value: DateRange;
  onChange: (value: DateRange) => void;
  min?: string;
  max?: string;
  disabled?: boolean;
  error?: string | null;
  hint?: string;
  className?: string;
}

/**
 * Two native date inputs rather than a bespoke calendar: the app never needs
 * more than an ISO range, and native pickers give locale, keyboard and mobile
 * behaviour that a hand-rolled calendar would have to re-earn.
 */
export const DateRangePicker = forwardRef<HTMLInputElement, DateRangePickerProps>(
  function DateRangePicker(
    { label, hideLabel, value, onChange, min, max, disabled, error, hint, className },
    ref,
  ) {
    const baseId = useId();
    const invalid =
      Boolean(error) ||
      Boolean(value.start && value.end && new Date(value.start) > new Date(value.end));

    const inputClass = cn(
      'h-9 rounded-md border border-line bg-surface px-2.5 text-dense-lg text-content',
      'hover:border-line-strong disabled:cursor-not-allowed disabled:bg-surface-sunken disabled:text-content-disabled',
      'aria-[invalid=true]:border-danger',
      focusRing,
    );

    return (
      <div className={cn('flex flex-col gap-1.5', className)}>
        <span
          id={`${baseId}-label`}
          className={cn('text-dense-lg font-medium text-content-secondary', hideLabel && 'sr-only')}
        >
          {label}
        </span>
        <div
          role="group"
          aria-labelledby={`${baseId}-label`}
          className="flex items-center gap-2 rounded-md"
        >
          <Icon name="calendar" size={15} className="text-content-muted" />
          <input
            ref={ref}
            type="date"
            aria-label={`${label} — start`}
            value={value.start ?? ''}
            min={min}
            max={value.end ?? max}
            disabled={disabled}
            aria-invalid={invalid || undefined}
            onChange={(event) => onChange({ ...value, start: event.target.value || null })}
            className={inputClass}
          />
          <span aria-hidden="true" className="text-content-muted">
            –
          </span>
          <input
            type="date"
            aria-label={`${label} — end`}
            value={value.end ?? ''}
            min={value.start ?? min}
            max={max}
            disabled={disabled}
            aria-invalid={invalid || undefined}
            onChange={(event) => onChange({ ...value, end: event.target.value || null })}
            className={inputClass}
          />
        </div>
        {hint && !error ? <p className="text-dense text-content-muted">{hint}</p> : null}
        {error ? (
          <p className="flex items-center gap-1 text-dense text-danger">
            <Icon name="alert-circle" size={13} />
            {error}
          </p>
        ) : null}
      </div>
    );
  },
);
